import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, ReferenceLine, BarChart, Bar,
} from 'recharts';
import { api, extractApiError } from '../api/client';
import Tooltip2 from '../components/Tooltip';
import { formatCurrency, formatNumber, downloadCSV, formatMonthLabel } from '../utils/format';
import { SkeletonChart, SkeletonMetricCard } from '../components/SkeletonLoader';
import type { Scenario, ScenarioAssumptions, MonthlyProjection } from '@shared/types';

const FIELD_TOOLTIPS: Record<string, string> = {
  monthlyFixedCosts: 'Fixed costs that don\'t change with user count: rent, full-time salaries, SaaS subscriptions, etc.',
  variableCostPerUnit: 'Cost you incur per active user per month: cloud infrastructure, support, payment processing fees.',
  initialUsers: 'Number of paying customers at the start of month 1 of your projection.',
  monthlyGrowthRate: 'Expected percentage of new users acquired relative to previous month\'s user count.',
  pricePerUnit: 'Average monthly revenue per customer/account (MRR per seat or subscription price).',
  churnRate: 'Percentage of active users who cancel or leave each month. 5% = typical early SaaS.',
  projectionMonths: 'How far into the future to model (1–36 months). 24 months is standard.',
  cac: 'Customer Acquisition Cost: total marketing + sales spend divided by new customers acquired.',
};

const DEFAULT_SCENARIOS: Omit<Scenario, 'id' | 'startup' | 'createdAt' | 'projections'>[] = [
  { name: 'Pessimistic', assumptions: { monthlyFixedCosts: 5000, variableCostPerUnit: 2, initialUsers: 20, monthlyGrowthRate: 0.08, pricePerUnit: 49, churnRate: 0.1, projectionMonths: 24, cac: 100 }, summary: undefined },
  { name: 'Base Case', assumptions: { monthlyFixedCosts: 5000, variableCostPerUnit: 2, initialUsers: 50, monthlyGrowthRate: 0.15, pricePerUnit: 49, churnRate: 0.06, projectionMonths: 24, cac: 80 }, summary: undefined },
  { name: 'Optimistic', assumptions: { monthlyFixedCosts: 5000, variableCostPerUnit: 2, initialUsers: 100, monthlyGrowthRate: 0.25, pricePerUnit: 49, churnRate: 0.03, projectionMonths: 24, cac: 60 }, summary: undefined },
];

const SCENARIO_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ec4899', '#14b8a6'];

function formatYAxis(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value}`;
}

function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ name: string; value: number; color: string }>; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#161627] border border-[#2a2a3e] rounded-lg p-3 shadow-xl">
      <p className="text-gray-400 text-xs mb-2">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }} className="text-sm">
          {p.name}: {formatCurrency(p.value)}
        </p>
      ))}
    </div>
  );
}

export default function FinancialsPage() {
  const { id: startupId } = useParams<{ id: string }>();
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [activeScenario, setActiveScenario] = useState<Scenario | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [sensitivity, setSensitivity] = useState({ growthRate: 0, price: 0, churn: 0 });
  const [isRunning, setIsRunning] = useState(false);
  const [runErrors, setRunErrors] = useState<Record<string, string>>({});
  const [showResults, setShowResults] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const fetchScenarios = useCallback(async () => {
    if (!startupId) return;
    setIsLoading(true);
    try {
      const { data } = await api.get(`/scenarios/startup/${startupId}`);
      const fetched: Scenario[] = data.data.scenarios;
      setScenarios(fetched);
      if (fetched.length > 0) {
        const base = fetched.find((s) => s.name.toLowerCase().includes('base')) ?? fetched[0];
        setActiveScenario(base);
        setIsLoading(false);
      } else {
        setIsLoading(false);
        // Auto-generate baseline from pipeline if available
        try {
          const { data: suData } = await api.get(`/startups/${startupId}`);
          if (suData.data.startup?.fromPipelineId) {
            const { data: genData } = await api.post(`/scenarios/generate-from-pipeline/${startupId}`, {});
            if (genData.data?.scenario) {
              setScenarios([genData.data.scenario]);
              setActiveScenario(genData.data.scenario);
              setShowResults(true);
            }
          }
        } catch {
          // silently skip — user can create manually
        }
      }
    } catch (err) {
      console.error(extractApiError(err).message);
      setIsLoading(false);
    }
  }, [startupId]);

  useEffect(() => { document.title = 'Financial Modeling — StartupScore'; }, []);

  useEffect(() => { fetchScenarios(); }, [fetchScenarios]);

  const handleAssumptionChange = (field: keyof ScenarioAssumptions, rawValue: string) => {
    if (!activeScenario) return;
    const value = parseFloat(rawValue) || 0;
    const divisor = ['monthlyGrowthRate', 'churnRate'].includes(field) ? 100 : 1;
    const updated: Scenario = {
      ...activeScenario,
      assumptions: { ...activeScenario.assumptions, [field]: value / divisor },
    };
    setActiveScenario(updated);
    setScenarios((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));

    // Debounced save + recalculate
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.put(`/scenarios/${updated.id}`, {
          name: updated.name,
          assumptions: updated.assumptions,
          startupId,
        });
        const saved = data.data.scenario as Scenario;
        setActiveScenario(saved);
        setScenarios((prev) => prev.map((s) => (s.id === saved.id ? saved : s)));
      } catch {
        // silently fail on auto-save
      }
    }, 300);
  };

  const createScenario = async () => {
    if (!newName.trim() || !startupId) return;
    setIsSaving(true);
    try {
      const template = DEFAULT_SCENARIOS[1]; // Base Case template
      const { data } = await api.post('/scenarios', {
        startupId,
        name: newName,
        assumptions: template.assumptions,
      });
      const created = data.data.scenario as Scenario;
      setScenarios((prev) => [...prev, created]);
      setActiveScenario(created);
      setNewName('');
      setShowAddForm(false);
    } catch (err) {
      alert(extractApiError(err).message);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteScenario = async (scenarioId: string) => {
    if (!confirm('Delete this scenario?')) return;
    await api.delete(`/scenarios/${scenarioId}`);
    const remaining = scenarios.filter((s) => s.id !== scenarioId);
    setScenarios(remaining);
    setActiveScenario(remaining[0] ?? null);
  };

  const validateAssumptions = (): boolean => {
    const errs: Record<string, string> = {};
    const a = activeScenario!.assumptions;
    if (!a.initialUsers || a.initialUsers <= 0) errs['initialUsers'] = 'Required (> 0)';
    if (!a.pricePerUnit || a.pricePerUnit <= 0) errs['pricePerUnit'] = 'Required (> 0)';
    if (!a.projectionMonths || a.projectionMonths < 1) errs['projectionMonths'] = 'Min 1 month';
    setRunErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleRunAnalysis = async () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!validateAssumptions() || !activeScenario || !startupId) return;
    setIsRunning(true);
    try {
      await api.put(`/scenarios/${activeScenario.id}`, {
        name: activeScenario.name, assumptions: activeScenario.assumptions, startupId,
      });
      const { data } = await api.post(`/scenarios/${activeScenario.id}/calculate`);
      const updated = data.data.scenario as Scenario;
      setActiveScenario(updated);
      setScenarios(prev => prev.map(s => s.id === updated.id ? updated : s));
      setShowResults(true);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err) {
      setRunErrors({ _global: extractApiError(err).message });
    } finally {
      setIsRunning(false);
    }
  };

  // Sensitivity-adjusted projections
  const sensitiveProjections = activeScenario?.projections.map((m) => ({
    ...m,
    adjustedRevenue: m.revenue * (1 + sensitivity.price / 100) * (1 + sensitivity.growthRate / 100),
  })) ?? [];

  const projections = activeScenario?.projections ?? [];
  const summary = activeScenario?.summary;

  const exportCSV = () => {
    if (!projections.length) return;
    const headers = ['Month', 'Users', 'Revenue', 'Fixed Costs', 'Variable Costs', 'Net Profit', 'Cash Flow'];
    const rows = projections.map((m) => [
      m.month, Math.round(m.users), m.revenue, m.fixedCosts, m.variableCosts, m.netProfit, m.cumulativeCashFlow,
    ]);
    downloadCSV([headers, ...rows], `${activeScenario?.name ?? 'scenario'}-projections.csv`);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Financial Modeling</h1>
          <p className="text-gray-400 mt-1">Model scenarios, project revenue, analyze unit economics</p>
        </div>
        {scenarios.length > 0 && (
          <button onClick={exportCSV} disabled={!projections.length} className="btn-secondary text-sm flex items-center gap-2">
            ⬇ Export CSV
          </button>
        )}
      </div>

      {/* ── Scenario Tabs ───────────────────────────────────── */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1">
        {scenarios.map((s, i) => (
          <div key={s.id} className="relative flex-shrink-0">
            <button
              onClick={() => setActiveScenario(s)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                activeScenario?.id === s.id
                  ? 'bg-brand-500/20 border-brand-500 text-brand-500'
                  : 'bg-transparent border-[#2a2a3e] text-gray-400 hover:text-white'
              }`}
              style={activeScenario?.id === s.id ? { borderColor: SCENARIO_COLORS[i] + '80', color: SCENARIO_COLORS[i] } : {}}
            >
              {s.name}
            </button>
            {scenarios.length > 1 && (
              <button
                onClick={() => deleteScenario(s.id)}
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#2a2a3e] text-gray-500 hover:text-red-400 text-xs flex items-center justify-center"
              >
                ×
              </button>
            )}
          </div>
        ))}
        {scenarios.length < 5 && (
          showAddForm ? (
            <div className="flex items-center gap-2 flex-shrink-0">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && createScenario()}
                placeholder="Scenario name"
                className="input-field text-sm w-32"
                autoFocus
              />
              <button onClick={createScenario} disabled={isSaving} className="btn-primary text-sm py-1.5">
                {isSaving ? '...' : 'Add'}
              </button>
              <button onClick={() => setShowAddForm(false)} className="text-gray-500 hover:text-white text-sm">✕</button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{
                background: 'rgba(124,111,247,0.1)',
                border: '1px dashed rgba(124,111,247,0.4)',
                color: '#7c6ff7',
              }}
            >
              + Add scenario
            </button>
          )
        )}
      </div>

      {isLoading ? (
        <div className="grid lg:grid-cols-2 gap-6">
          <SkeletonChart />
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => <SkeletonMetricCard key={i} />)}
          </div>
        </div>
      ) : !activeScenario ? (
        <div className="card text-center py-16">
          <p className="text-gray-400 mb-4">No scenarios yet. Create your first one to start modeling.</p>
          <button onClick={() => setShowAddForm(true)} className="btn-primary">Create first scenario</button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* ── Section A: Assumptions ──────────────────────── */}
          <div className="card">
            <h2 className="text-lg font-bold text-white mb-4">Assumptions — {activeScenario.name}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { key: 'monthlyFixedCosts', label: 'Monthly Fixed Costs ($)', isPercent: false },
                { key: 'variableCostPerUnit', label: 'Variable Cost per User ($)', isPercent: false },
                { key: 'initialUsers', label: 'Initial Users', isPercent: false },
                { key: 'monthlyGrowthRate', label: 'Monthly Growth Rate (%)', isPercent: true },
                { key: 'pricePerUnit', label: 'Price per User/Month ($)', isPercent: false },
                { key: 'churnRate', label: 'Monthly Churn Rate (%)', isPercent: true },
                { key: 'projectionMonths', label: 'Projection Period (months)', isPercent: false },
                { key: 'cac', label: 'Customer Acquisition Cost ($)', isPercent: false },
              ].map(({ key, label, isPercent }) => {
                const rawVal = activeScenario.assumptions[key as keyof ScenarioAssumptions] as number;
                const displayVal = isPercent ? rawVal * 100 : rawVal;
                return (
                  <div key={key}>
                    <div className="flex items-center gap-1 mb-1">
                      <label className="label mb-0 text-xs">{label}</label>
                      <Tooltip2 content={FIELD_TOOLTIPS[key] ?? ''} />
                    </div>
                    <input
                      type="number"
                      value={displayVal.toFixed(isPercent ? 1 : 0)}
                      onChange={(e) => handleAssumptionChange(key as keyof ScenarioAssumptions, isPercent ? String(parseFloat(e.target.value)) : e.target.value)}
                      min={0}
                      max={key === 'projectionMonths' ? 36 : undefined}
                      step={isPercent ? 0.1 : 1}
                      className="input-field text-sm"
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Run Analysis CTA ─────────────────────────────── */}
          <div>
            {runErrors['_global'] && (
              <p className="text-red-400 text-sm mb-2">{runErrors['_global']}</p>
            )}
            <button
              onClick={handleRunAnalysis}
              disabled={isRunning}
              style={{
                width: '100%', height: 56, borderRadius: 14,
                background: isRunning ? '#2a2a3e' : 'linear-gradient(135deg, #7c6ff7, #00d4ff)',
                border: 'none', color: '#fff', fontSize: 17, fontWeight: 700, cursor: isRunning ? 'not-allowed' : 'pointer',
                transition: 'opacity 0.2s', opacity: isRunning ? 0.7 : 1,
              }}
            >
              {isRunning ? '⏳ Running Analysis...' : '▶ Run Financial Analysis'}
            </button>
          </div>

          {/* ── Results section ───────────────────────────────── */}
          <div ref={resultsRef} className={showResults || (activeScenario?.projections?.length ?? 0) > 0 ? 'wizard-slide-fwd' : ''}>

          {/* ── Summary Metrics ──────────────────────────────── */}
          {summary && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  label: 'Projected ARR', value: formatCurrency(summary.projectedARR, true),
                  sub: `End of month ${activeScenario.assumptions.projectionMonths}`,
                  color: 'text-green-400',
                },
                {
                  label: 'Break-even Month',
                  value: summary.breakEvenMonth ? `Month ${summary.breakEvenMonth}` : 'Not in period',
                  sub: 'Cumulative cash flow positive',
                  color: summary.breakEvenMonth ? 'text-brand-500' : 'text-red-400',
                },
                {
                  label: '12m Revenue',
                  value: formatCurrency(summary.totalRevenue12m, true),
                  sub: `12m costs: ${formatCurrency(summary.totalCosts12m, true)}`,
                  color: 'text-white',
                },
                {
                  label: 'Max Drawdown',
                  value: formatCurrency(summary.maxDrawdown, true),
                  sub: 'Worst cumulative cash position',
                  color: summary.maxDrawdown < 0 ? 'text-red-400' : 'text-green-400',
                },
              ].map((metric) => (
                <div key={metric.label} className="card">
                  <p className="text-gray-500 text-xs mb-1">{metric.label}</p>
                  <p className={`text-2xl font-bold ${metric.color} mb-1`}>{metric.value}</p>
                  <p className="text-gray-600 text-xs">{metric.sub}</p>
                </div>
              ))}
            </div>
          )}

          {/* ── Chart 1: Revenue vs Costs ──────────────────── */}
          <div className="card">
            <h2 className="text-lg font-bold text-white mb-4">Revenue vs Costs Over Time</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={projections} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" />
                <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={{ fill: '#6b7280', fontSize: 11 }} />
                <YAxis tickFormatter={formatYAxis} tick={{ fill: '#6b7280', fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: '12px', color: '#9ca3af' }} />
                {summary?.breakEvenMonth && (
                  <ReferenceLine
                    x={summary.breakEvenMonth}
                    stroke="#a855f7"
                    strokeDasharray="4 4"
                    label={{ value: `Break-even: M${summary.breakEvenMonth}`, fill: '#a855f7', fontSize: 11, position: 'top' }}
                  />
                )}
                <Line type="monotone" dataKey="revenue" stroke="#22c55e" strokeWidth={2} dot={false} name="Revenue" />
                <Line type="monotone" dataKey="totalCosts" stroke="#ef4444" strokeWidth={2} dot={false} name="Total Costs" />
                <Line type="monotone" dataKey="netProfit" stroke="#6366f1" strokeWidth={2} dot={false} name="Net Profit" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* ── Chart 2: Cumulative Cash Flow ──────────────── */}
          <div className="card">
            <h2 className="text-lg font-bold text-white mb-4">Cumulative Cash Flow</h2>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={projections} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <defs>
                  <linearGradient id="cashflowGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="cashflowNeg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" />
                <XAxis dataKey="month" tickFormatter={formatMonthLabel} tick={{ fill: '#6b7280', fontSize: 11 }} />
                <YAxis tickFormatter={formatYAxis} tick={{ fill: '#6b7280', fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine y={0} stroke="#4b5563" strokeWidth={2} label={{ value: '$0', fill: '#6b7280', fontSize: 11, position: 'right' }} />
                {summary?.breakEvenMonth && (
                  <ReferenceLine x={summary.breakEvenMonth} stroke="#a855f7" strokeDasharray="4 4" />
                )}
                <Area type="monotone" dataKey="cumulativeCashFlow" stroke="#6366f1" fill="url(#cashflowGrad)" strokeWidth={2} name="Cumulative Cash Flow" />
              </AreaChart>
            </ResponsiveContainer>
            <p className="text-center text-xs text-gray-500 mt-2">
              {summary?.breakEvenMonth
                ? `Break-even: Month ${summary.breakEvenMonth}`
                : 'Does not break even in this period'}
            </p>
          </div>

          {/* ── Chart 3: Scenario Comparison ──────────────── */}
          {scenarios.length >= 2 && (
            <div className="card">
              <h2 className="text-lg font-bold text-white mb-4">Scenario Comparison — Revenue at Milestones</h2>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={[
                    { milestone: 'Month 3', ...Object.fromEntries(scenarios.map((s) => [s.name, s.projections.find((m) => m.month === 3)?.revenue ?? 0])) },
                    { milestone: 'Month 6', ...Object.fromEntries(scenarios.map((s) => [s.name, s.projections.find((m) => m.month === 6)?.revenue ?? 0])) },
                    { milestone: 'Month 12', ...Object.fromEntries(scenarios.map((s) => [s.name, s.projections.find((m) => m.month === 12)?.revenue ?? 0])) },
                  ]}
                  margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" />
                  <XAxis dataKey="milestone" tick={{ fill: '#6b7280', fontSize: 11 }} />
                  <YAxis tickFormatter={formatYAxis} tick={{ fill: '#6b7280', fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px', color: '#9ca3af' }} />
                  {scenarios.map((s, i) => (
                    <Bar key={s.id} dataKey={s.name} fill={SCENARIO_COLORS[i]} radius={[4, 4, 0, 0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* ── Chart 4: Unit Economics ────────────────────── */}
          {summary?.unitEconomics && (
            <div className="card">
              <h2 className="text-lg font-bold text-white mb-4">Unit Economics</h2>
              {/* P2-20: CAC validation warning */}
              {summary.unitEconomics.ltvToCacRatio < 1 && (
                <div style={{
                  background: '#ef444410', border: '1px solid #ef444430',
                  borderRadius: 10, padding: '10px 16px', marginBottom: 16,
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <span style={{ fontSize: 18 }}>🚨</span>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#ef4444' }}>CAC exceeds LTV</p>
                    <p style={{ fontSize: 12, color: '#9ca3af' }}>
                      You're spending {formatCurrency(summary.unitEconomics.cac)} to acquire a customer worth {formatCurrency(summary.unitEconomics.ltv)}.
                      Reduce CAC or increase pricing/retention to reach profitability.
                    </p>
                  </div>
                </div>
              )}
              {summary.unitEconomics.ltvToCacRatio >= 1 && summary.unitEconomics.ltvToCacRatio < 3 && (
                <div style={{
                  background: '#f59e0b10', border: '1px solid #f59e0b30',
                  borderRadius: 10, padding: '10px 16px', marginBottom: 16,
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <span style={{ fontSize: 18 }}>⚠️</span>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#f59e0b' }}>LTV:CAC ratio is below healthy threshold</p>
                    <p style={{ fontSize: 12, color: '#9ca3af' }}>
                      Your ratio is {summary.unitEconomics.ltvToCacRatio.toFixed(1)}x. Aim for 3x+ for sustainable growth.
                      Consider reducing acquisition costs or improving retention.
                    </p>
                  </div>
                </div>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'LTV (Lifetime Value)', value: formatCurrency(summary.unitEconomics.ltv), sub: 'Revenue per customer lifetime', color: 'text-green-400' },
                  { label: 'CAC', value: formatCurrency(summary.unitEconomics.cac), sub: 'Cost to acquire one customer', color: 'text-white' },
                  {
                    label: 'LTV:CAC Ratio',
                    value: `${summary.unitEconomics.ltvToCacRatio.toFixed(1)}x`,
                    sub: summary.unitEconomics.ltvToCacRatio >= 3 ? '✅ Healthy (>3x)' : summary.unitEconomics.ltvToCacRatio >= 1 ? '⚠️ Low (aim for 3x+)' : '❌ Below breakeven',
                    color: summary.unitEconomics.ltvToCacRatio >= 3 ? 'text-green-400' : summary.unitEconomics.ltvToCacRatio >= 1 ? 'text-yellow-400' : 'text-red-400',
                  },
                  { label: 'Payback Period', value: `${summary.unitEconomics.paybackPeriodMonths.toFixed(1)} mo`, sub: 'Months to recoup CAC', color: 'text-brand-500' },
                ].map((m) => (
                  <div key={m.label} className="bg-[#0f0f1a] rounded-xl p-4">
                    <p className="text-gray-500 text-xs mb-1">{m.label}</p>
                    <p className={`text-2xl font-bold ${m.color} mb-1`}>{m.value}</p>
                    <p className="text-gray-600 text-xs">{m.sub}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Section C: Sensitivity Analysis ───────────── */}
          <div className="card">
            <h2 className="text-lg font-bold text-white mb-4">Sensitivity Analysis</h2>
            <p className="text-gray-400 text-sm mb-4">Adjust sliders to see how Year 1 revenue changes.</p>
            <div className="space-y-4">
              {[
                { key: 'growthRate' as const, label: 'Growth Rate', value: sensitivity.growthRate },
                { key: 'price' as const, label: 'Price', value: sensitivity.price },
                { key: 'churn' as const, label: 'Churn Rate', value: sensitivity.churn },
              ].map(({ key, label, value }) => (
                <div key={key}>
                  <div className="flex justify-between mb-1">
                    <label className="label mb-0 text-sm">{label} adjustment</label>
                    <span className={`text-sm font-medium ${value > 0 ? 'text-green-400' : value < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                      {value > 0 ? '+' : ''}{value}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min={-50}
                    max={50}
                    value={value}
                    onChange={(e) => setSensitivity({ ...sensitivity, [key]: parseInt(e.target.value) })}
                    className="w-full accent-brand-500"
                  />
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>-50%</span>
                    <span>0</span>
                    <span>+50%</span>
                  </div>
                </div>
              ))}
              <div className="mt-4 bg-[#0f0f1a] rounded-xl p-4">
                <p className="text-gray-400 text-sm mb-1">Adjusted Year 1 Revenue</p>
                <p className="text-2xl font-bold text-white">
                  {formatCurrency(sensitiveProjections.slice(0, 12).reduce((s, m) => s + (m.adjustedRevenue ?? m.revenue), 0))}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  vs base: {formatCurrency(projections.slice(0, 12).reduce((s, m) => s + m.revenue, 0))}
                </p>
              </div>
            </div>
          </div>

          {/* ── Section D: Monthly Table ──────────────────── */}
          <div className="card overflow-x-auto" style={{ display: (showResults || (activeScenario?.projections?.length ?? 0) > 0) ? undefined : 'none' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white">Monthly Projections</h2>
              <button onClick={exportCSV} className="btn-secondary text-xs">Export CSV</button>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#2a2a3e]">
                  {['Month', 'Users', 'Revenue', 'Fixed Costs', 'Variable Costs', 'Net Profit', 'Cash Flow'].map((h) => (
                    <th key={h} className="text-left py-2 px-3 text-gray-500 font-medium text-xs">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {projections.map((m: MonthlyProjection) => (
                  <tr
                    key={m.month}
                    className={`border-b border-[#2a2a3e] hover:bg-[#1a1a2e] transition-colors ${
                      summary?.breakEvenMonth === m.month ? 'bg-brand-500/5 border-brand-500/30' : ''
                    }`}
                  >
                    <td className="py-2 px-3 text-gray-400">
                      {m.month}
                      {summary?.breakEvenMonth === m.month && (
                        <span className="ml-1 text-xs text-brand-500">✓ BE</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-white">{formatNumber(m.users)}</td>
                    <td className="py-2 px-3 text-green-400">{formatCurrency(m.revenue)}</td>
                    <td className="py-2 px-3 text-gray-400">{formatCurrency(m.fixedCosts)}</td>
                    <td className="py-2 px-3 text-gray-400">{formatCurrency(m.variableCosts)}</td>
                    <td className={`py-2 px-3 font-medium ${m.netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatCurrency(m.netProfit)}
                    </td>
                    <td className={`py-2 px-3 font-medium ${m.cumulativeCashFlow >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatCurrency(m.cumulativeCashFlow)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#2a2a3e] font-medium">
                  <td className="py-2 px-3 text-gray-400">Total</td>
                  <td className="py-2 px-3" />
                  <td className="py-2 px-3 text-green-400">{formatCurrency(projections.reduce((s, m) => s + m.revenue, 0))}</td>
                  <td className="py-2 px-3 text-gray-400">{formatCurrency(projections.reduce((s, m) => s + m.fixedCosts, 0))}</td>
                  <td className="py-2 px-3 text-gray-400">{formatCurrency(projections.reduce((s, m) => s + m.variableCosts, 0))}</td>
                  <td className="py-2 px-3 text-white">{formatCurrency(projections.reduce((s, m) => s + m.netProfit, 0))}</td>
                  <td className="py-2 px-3" />
                </tr>
              </tfoot>
            </table>
          </div>
          </div>{/* end results wrapper */}
        </div>
      )}
    </div>
  );
}
